import { IJob, IUpdateJobPayload } from '~/utils/interface/job';
import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIn, useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { cast, handleApiError, notify } from '~/utils/helper';
import { DeleteCircle, PlusIcon, TrashIcon, ArrowLeftDoubleIcon } from '~/assets/icons';
import { handleGetJob } from '~/thunks/job/jobThunk';
import { BaseDatePicker, BaseRow } from '~/components/common';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LOCATION } from '~/utils/constants/areaScope';
import { NumericFormat } from 'react-number-format';
import { handleUpdateJob } from '~/thunks/job/jobThunk';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';
import { MASTER_DATA_MATERIAL_TYPES } from '~/utils/constants/master';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { areaScopeActions } from '~/thunks/areaScope/areaScopeSlice';

const initialValues: IUpdateJobPayload = {
  job: {
    moisturePoint: {
      moisturePoints: [],
    },
    materialType: [],
  },
};

const AreaScopeMoisturePoints = () => {
  const dispatch = useAppDispatch();
  const [job, setJob] = useState<IJob>();
  const { t } = useTranslation('admin/job');
  const [titleDelete, setTitleDelete] = useState<boolean>(false);
  const { areaScopes, openAlertDeleteMoisturePoints } = useAppSelector((state) => state.areaScope);
  const { materialTypes } = useAppSelector((state) => state.master);
  const { id, index } = useParams();
  const location = useLocation();
  const { area, checkAddScope } = location.state || {};
  const navigate = useNavigate();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const submitValues = { ...values };
      const newItems = values.job.moisturePoint?.moisturePoints || [];
      let currentMoisturePoints: any = [];

      if (index === 'create') {
        if (Array.isArray(job?.moisturePoint?.moisturePoints)) {
          currentMoisturePoints = [...job.moisturePoint.moisturePoints];
        } else if (job?.moisturePoint?.moisturePoints) {
          currentMoisturePoints = [job.moisturePoint.moisturePoints];
        }
        currentMoisturePoints.push(...newItems);
      } else {
        currentMoisturePoints = [...(job?.moisturePoint?.moisturePoints || [])];
        const newItem = newItems[0];

        if (newItem && index !== undefined) {
          currentMoisturePoints[index] = newItem;
        } else {
          currentMoisturePoints.splice(index, 1);
        }
      }

      submitValues.job.moisturePoint.moisturePoints = currentMoisturePoints;
      // Collect new material types from the form
      const newMaterialTypes: any = [];
      submitValues.job.moisturePoint.moisturePoints?.forEach((drybook: any) => {
        const materialType = drybook.materialType;

        // Check if the materialType is new
        const isNewType = !materialTypes?.some((type) => type.name === materialType);
        if (isNewType && materialType) {
          newMaterialTypes.push(materialType);
        }
      });

      submitValues.job.materialType = [...newMaterialTypes];
      try {
        await dispatch(
          handleUpdateJob({
            id: Number(job?.id),
            params: { ...submitValues },
          })
        ).unwrap();
        if (index === 'create') {
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        } else if (titleDelete) {
          notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
        } else {
          notify('success', t('editSuccess', { ns: 'admin/common' }));
        }
        setTitleDelete(false);
        if (id) {
          navigate(-1);
        }
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const job = await dispatch(handleGetJob(id.toString())).unwrap();
        setJob(job);

        if (job?.id) {
          dispatch(handleGetAreaScopeList({ jobId: job.id })).catch(console.error);
          dispatch(
            handleGetMasterData({
              type: [MASTER_DATA_MATERIAL_TYPES],
            })
          );
        }
        if (index === 'create') {
          addNewDryBookField();
        } else {
          form.setFieldValue(
            'job.moisturePoint.moisturePoints',
            cast(index !== undefined ? [job?.moisturePoint?.moisturePoints[index]] : [])
          );
        }
      } catch (e) {
        handleApiError(e);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSetOpen = () => {
    form.resetForm();
    if (id) {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const job = await dispatch(handleGetJob(id.toString())).unwrap();
          job.jobTypeId = job.jobType.id;
          setJob(job);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [id, dispatch]);

  const addNewDryBookField = () => {
    const currentDate = new Date();
    const formattedDate = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${currentDate.getFullYear()}`;

    const newField = {
      area: checkAddScope ? area : undefined,
      location: undefined,
      moisturePoint: undefined,
      dryStandard: undefined,
      materialType: undefined,
      goal: undefined,
      readings: [
        {
          date: formattedDate,
          mp: undefined,
          area: checkAddScope ? area : undefined,
          mt: undefined,
          location: undefined,
          value: undefined,
          goal: undefined,
        },
      ],
    };

    form.setFieldValue('job.moisturePoint.moisturePoints', [
      ...(form.values.job.moisturePoint.moisturePoints ?? []),
      newField,
    ]);
  };

  const addNewDryBookItem = (index: number) => {
    const newDryBookFields = [...form.values.job.moisturePoint.moisturePoints];
    const currentDate = new Date();
    const formattedDate = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${currentDate.getFullYear()}`;

    const currentReadings = [...(newDryBookFields[index].readings || [])];
    const baseReading = currentReadings[0] || {};

    const newReading = {
      date: formattedDate,
      mp: baseReading.mp,
      area: baseReading.area ?? newDryBookFields[index].area ?? '',
      mt: baseReading.mt,
      location: baseReading.location,
      value: '',
      goal: baseReading.goal,
    };

    newDryBookFields[index] = {
      ...newDryBookFields[index],
      readings: [...currentReadings, newReading],
    };

    form.setFieldValue('job.moisturePoint.moisturePoints', newDryBookFields);
  };

  const updateMoisturePoint = (form: any, index: number, moisturePointValue: string) => {
    const updatedMoisturePoint = parseFloat(moisturePointValue) || 0;
    // Use forEach to update the 'mp' field in each reading
    form.values?.job.moisturePoint.moisturePoints?.forEach((drybook: any, idx: number) => {
      if (idx === index) {
        drybook.moisturePoint = updatedMoisturePoint.toString();

        drybook.readings?.forEach((reading: any) => {
          // Set the 'mp' value to the moisturePoint value
          reading.mp = updatedMoisturePoint.toString();
        });
      }
    });

    // Return the updated form values after modifying the array
    return form.values?.job?.moisturePoint.moisturePoints;
  };

  const handleAreaChange = (value: string, drybookIndex: number) => {
    const updatedReadings = form.values?.job.moisturePoint?.moisturePoints?.[drybookIndex]?.readings?.map(
      (reading: any) => ({
        ...reading,
        area: value,
      })
    );

    form.setFieldValue(`job.moisturePoint.moisturePoints[${drybookIndex}].readings`, updatedReadings);
    form.setFieldValue(`job.moisturePoint.moisturePoints[${drybookIndex}].area`, value);
  };

  const handleLocationChange = (value: string, drybookIndex: number) => {
    const updatedReadings = form.values.job.moisturePoint?.moisturePoints?.[drybookIndex]?.readings?.map(
      (reading: any) => ({
        ...reading,
        location: value,
      })
    );

    form.setFieldValue(`job.moisturePoint.moisturePoints[${drybookIndex}].readings`, updatedReadings);
    form.setFieldValue(`job.moisturePoint.moisturePoints[${drybookIndex}].location`, value);
  };

  const updateDrybookGoal = (form: any, index: number, dryStandardValue: string) => {
    const updatedDryStandard = parseFloat(dryStandardValue) || 0;

    let goal = '';

    if (updatedDryStandard) {
      const calculatedGoal = updatedDryStandard * 1.1;

      goal = Number.isInteger(calculatedGoal) ? calculatedGoal.toString() : calculatedGoal.toFixed(2);
    }

    // Use forEach to modify the drybook array
    form.values?.job?.moisturePoint?.moisturePoints?.forEach((drybook: any, idx: number) => {
      if (idx === index) {
        // Update the dryStandard and goal for the specific drybook
        drybook.dryStandard = updatedDryStandard;
        drybook.goal = goal;

        // Update the readings array with the new goal
        drybook.readings?.forEach((reading: any) => {
          reading.goal = goal;
        });
      }
    });

    // Return the updated form values after modifying the array
    return form.values?.job?.moisturePoint?.moisturePoints;
  };

  const updateReadingsGoal = (index: number, goalValue: string) => {
    form.values?.job?.moisturePoint?.moisturePoints?.forEach((drybook: any, idx: number) => {
      if (idx === index) {
        drybook.goal = goalValue;
        drybook.readings?.forEach((reading: any) => {
          reading.goal = goalValue;
        });
      }
    });

    return form.values?.job?.moisturePoint?.moisturePoints;
  };

  const handleDeleteDryBookItem = (idx: number, index: number) => {
    const drybookReadings: any = form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings;

    // If dh readings exist and the idx is valid, remove the item at idx
    if (drybookReadings && idx >= 1 && idx < drybookReadings.length) {
      const updatedReadings = drybookReadings.filter((_: any, i: any) => i !== idx);

      form.setFieldValue(`job.moisturePoint.moisturePoints[${index}].readings`, updatedReadings);
    }
  };

  const handleDeleteDryBookFields = (index: number) => {
    const updatedFields = [...(form.values.job.moisturePoint.moisturePoints ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('job.moisturePoint.moisturePoints', updatedFields);
    dispatch(areaScopeActions.changeOpenAlertDeleteMoisturePoints(false));
  };

  const handleDelete = async () => {
    dispatch(areaScopeActions.changeOpenAlertDeleteMoisturePoints(true));
  };

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(areaScopeActions.changeOpenAlertDeleteMoisturePoints(false));
  }, [dispatch]);

  const onDeleteSelected = useCallback(async () => {
    setTitleDelete(true);
    await handleDeleteDryBookFields(Number(index));
    await form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <Box>
      <Box className='flex flex-row items-center justify-between text-[17px]'>
        <Box>
          <IconButton onClick={handleSetOpen} className='p-0 w-[40px] h-[40px]'>
            <ArrowLeftDoubleIcon className='w-[11px] mb-[5px]' />
          </IconButton>
          {t('editMoisturePoint')}
        </Box>
      </Box>
      <Box>
        {/* drybookk */}

        {(form.values.job?.moisturePoint?.moisturePoints || []).map((field: any, index: any) => (
          <Box className='mb-[20px] p-7' key={index}>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('scope.area')}</InputLabel>
                <Autocomplete
                  freeSolo
                  options={areaScopes?.map((type: any) => type.name) ?? []}
                  value={checkAddScope ? area : cast(form.values?.job?.moisturePoint?.moisturePoints[index]?.area)}
                  onChange={(e, val) => handleAreaChange(val, index)}
                  onInputChange={(e, val) => {
                    handleAreaChange(val, index);
                  }}
                  renderInput={(params) => <TextField {...params} placeholder={t('scope.area')} fullWidth />}
                  disabled={checkAddScope}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('scope.location')}</InputLabel>
                <Autocomplete
                  freeSolo
                  options={LOCATION?.map((type: any) => type.name) ?? []}
                  value={cast(form.values?.job?.moisturePoint?.moisturePoints?.[index]?.location)}
                  onChange={(e, val) => handleLocationChange(val, index)}
                  onInputChange={(e, val) => {
                    handleLocationChange(val, index);
                  }}
                  renderInput={(params) => <TextField {...params} placeholder={t('scope.location')} fullWidth />}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>Moisture Point</InputLabel>
                <TextField
                  name={`job.moisturePoint.moisturePoints.[${index}].moisturePoint`}
                  placeholder='Moisture Point'
                  value={cast(form.values?.job?.moisturePoint?.moisturePoints?.[index]?.moisturePoint) ?? ''}
                  onChange={(e) => {
                    form.handleChange(e);
                    const updatedDrybook = updateMoisturePoint(form, index, e.target.value);
                    form.setFieldValue('job.moisturePoint.moisturePoints', updatedDrybook);
                  }}
                  error={Boolean(getIn(form.errors, `job.moisturePoint.moisturePoints.[${index}].moisturePoint`))}
                  helperText={getIn(form.errors, `job.moisturePoint.moisturePoints.[${index}].moisturePoint`)}
                  fullWidth
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>Dry Standard</InputLabel>
                <NumericFormat
                  customInput={TextField}
                  name={`job.moisturePoint.moisturePoints.[${index}].dryStandard`}
                  placeholder='Dry Standard'
                  value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.dryStandard ?? ''}
                  onValueChange={(val) => {
                    const updatedDrybook = updateDrybookGoal(form, index, val.floatValue + '');
                    form.setFieldValue('job.moisturePoint.moisturePoints', updatedDrybook);
                  }}
                  thousandSeparator={true}
                  fullWidth
                  decimalScale={2}
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>Material Type</InputLabel>
                <Autocomplete
                  freeSolo
                  options={materialTypes?.map((type: any) => type.name) ?? []} // Extract titles for display
                  value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.materialType || ''}
                  onChange={(event, newValue) => {
                    const updatedReadings = form.values.job.moisturePoint?.moisturePoints[index]?.readings?.map(
                      (reading: any) => ({
                        ...reading,
                        mt: newValue,
                      })
                    );
                    form.setFieldValue(`job.moisturePoint.moisturePoints[${index}].materialType`, newValue);
                    form.setFieldValue(`job.moisturePoint.moisturePoints[${index}].readings`, updatedReadings);
                  }}
                  onInputChange={(event, newInputValue) => {
                    const updatedReadings = form.values.job.moisturePoint?.moisturePoints[index]?.readings?.map(
                      (reading: any) => ({
                        ...reading,
                        mt: newInputValue,
                      })
                    );
                    form.setFieldValue(`job.moisturePoint.moisturePoints[${index}].materialType`, newInputValue);
                    form.setFieldValue(`job.moisturePoint.moisturePoints[${index}].readings`, updatedReadings);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder='Select or enter a Material Type' fullWidth />
                  )}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>Goal</InputLabel>
                <NumericFormat
                  className='bg-emerald-100 rounded-lg'
                  customInput={TextField}
                  name={`job.moisturePoint.moisturePoints.[${index}].goal`}
                  placeholder='Goal'
                  value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.goal ?? ''}
                  onValueChange={(val) => {
                    const updated = updateReadingsGoal(index, val.floatValue + '');
                    form.setFieldValue('job.moisturePoint.moisturePoints', updated);
                  }}
                  thousandSeparator={true}
                  fullWidth
                  decimalScale={2}
                />
              </Box>
            </BaseRow>

            {(field?.readings || []).map((reading: any, idx: any) => {
              const drybookPath = `job.moisturePoint.moisturePoints[${index}].readings[${idx}]`;

              return (
                <Box key={`${index}-${idx}`}>
                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Date</InputLabel>}
                      <BaseDatePicker
                        value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.date ?? ''}
                        onChange={(date) => {
                          if (date) {
                            const selectedDate = dayjs(date).format('MMM DD, YYYY');
                            form.setFieldValue(`${drybookPath}.date`, selectedDate);
                          }
                        }}
                      />
                    </Box>

                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>MP</InputLabel>}
                      <TextField
                        name={`${drybookPath}.mp`}
                        value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.mp ?? ''}
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Area</InputLabel>}
                      <TextField
                        name={`${drybookPath}.area`}
                        value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.area ?? ''}
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>MT</InputLabel>}
                      <TextField
                        name={`${drybookPath}.mt`}
                        value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.mt ?? ''}
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Location</InputLabel>}
                      <TextField
                        name={`${drybookPath}.location`}
                        value={
                          form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.location ?? ''
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Moisture Reading</InputLabel>}
                      <TextField
                        name={`${drybookPath}.value`}
                        placeholder='Moisture Reading'
                        value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.value ?? ''}
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-full'>
                      {idx === 0 && <InputLabel>Goal</InputLabel>}
                      <TextField
                        name={`${drybookPath}.goal`}
                        value={form.values?.job?.moisturePoint?.moisturePoints?.[index]?.readings?.[idx]?.goal ?? ''}
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Box>
                    <Box className='w-[5px]'>
                      {idx === 0 && <Box className='w-[5px] mt-[30px]'> </Box>}
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
                        onClick={() => handleDeleteDryBookItem(idx, index)}
                      />
                    </Box>
                  </BaseRow>
                  {idx === field.readings.length - 1 && (
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                      <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                      <Grid className='flex justify-end' item xs={12} sm={4}>
                        <Button
                          onClick={() => addNewDryBookItem(index)}
                          className='whitespace-nowrap'
                          startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
      <Box className='w-full flex gap-[10px] justify-end'>
        <Button
          className={`visibility: ${index !== 'create' ? 'visible' : 'hidden'}`}
          onClick={() => handleDelete()}
          variant='text'
          color='error'
          disableElevation
          startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='red' />}
        >
          {t('delete', { ns: 'admin/common' })}
        </Button>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen()}>
          {t('trackTime.cancel')}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('trackTime.save')}
        </Button>
      </Box>

      <ModalAlertDelete
        open={openAlertDeleteMoisturePoints}
        description={t('alertDescriptionDeleteMoisturePoint')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelected}
      />
    </Box>
  );
};

export default AreaScopeMoisturePoints;
